import * as Sentry from '@sentry/react';
import { useNavigate } from '@tanstack/react-router';
import { type ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  FUNNEL_PATH_BY_STEP,
  resolvePhaseTarget,
} from '~/features/content-enrichment/utils/phase-funnel-mapping';
import { useConnectedConnectors } from '~/features/onboarding/hooks/use-connections';

import { useExperimentDraft } from '../../hooks/use-experiment-draft';
import { useResumePrerequisites } from '../../hooks/use-resume-prerequisites';
import { clearWrongSubsidiaryAttempt } from '../../hooks/use-wrong-subsidiary-redirect';
import { useCreateExperimentStore } from '../../store/create-experiment.store';
import type { ExperimentChannel } from '../../types/create-experiment.types';
import type { DraftExperiment } from '../../types/experiment-draft.types';
import { hydrateFromDraft } from '../../utils/hydrate-draft';
import { stageToStep } from '../../utils/stage-to-step';
import { ExperimentLoadError } from '../detail/experiment-load-error';

import { ResumeOverlay } from './resume-overlay';

/** Channel code we fall back to when the draft DTO doesn't carry `channel` (rollout window). */
const FALLBACK_CHANNEL_CODE = 'GOOGLE_MERCHANT';

interface Props {
  /**
   * Draft id from the route's `?expId=` search param. `null`/`undefined`
   * means a fresh wizard run — children render as-is, no resume.
   */
  expId: string | null | undefined;
  children: ReactNode;
}

/**
 * Gates the configure route on draft hydration when `?expId=` is present.
 *
 * Phase 1: fetch the draft DTO.
 * Phase 2: fan out the catalog prereqs needed to hydrate + render the
 *          target step (filter defs always; kpis when target is 4 or 5).
 * Phase 3: dispatch `store.hydrate()` once and unmount the overlay.
 *
 * Idempotent — the store's own `draftId` doubles as the hydration marker:
 * after `hydrate(...)` runs, `store.draftId === expId`. Background refetches
 * of the draft query short-circuit the effect on that equality so they
 * cannot stomp the user's local edits.
 */
export function ResumeExperimentLoader({ expId, children }: Props) {
  const { t } = useTranslation('experiments');
  const navigate = useNavigate();
  const hydrate = useCreateExperimentStore((s) => s.hydrate);
  const storedDraftId = useCreateExperimentStore((s) => s.draftId);

  // Fast-path — no expId means render the wizard normally (fresh-create flow).
  const draftQuery = useExperimentDraft(expId);

  // Connection fallback — only used during the rollout window where the
  // draft DTO may omit `channel`. We resolve a Google connection from the
  // tenant's connected list to keep downstream catalogs working.
  const { data: connectedConnectors } = useConnectedConnectors();
  const fallbackConnectionId =
    connectedConnectors?.find((c) => c.code === FALLBACK_CHANNEL_CODE)?.connectionId ?? null;

  // Channel is resolved BEFORE prereqs run since the prereq hook needs the
  // connection id + connector code to fetch filter defs / kpis.
  const resolvedChannel = resolveChannel(draftQuery.data, fallbackConnectionId);
  const targetStep = stageToStep(draftQuery.data?.stage);

  const prereqs = useResumePrerequisites({
    channel: expId ? resolvedChannel : null,
    targetStep,
    enabled: !!expId,
  });

  // -- Error path: draft fetch failed ----------------------------------
  // Capture for telemetry only — the UI is handled by <ExperimentLoadError>
  // below, which turns a cross-subsidiary 404/403 into a deep-link-preserving
  // recovery instead of dead-ending back on /experiments.
  useEffect(() => {
    if (!expId || !draftQuery.isError) return;
    Sentry.captureException(draftQuery.error, {
      tags: { feature: 'experiments-resume', stage: 'fetch-draft' },
      extra: { expId },
    });
  }, [expId, draftQuery.isError, draftQuery.error]);

  // Clear the wrong-subsidiary auto-switch marker once the draft loads, so a
  // later re-open of this shared link can auto-switch again.
  useEffect(() => {
    if (expId && draftQuery.isSuccess) clearWrongSubsidiaryAttempt(expId);
  }, [expId, draftQuery.isSuccess]);

  // -- Error path: blocking prereq failed ------------------------------
  useEffect(() => {
    if (!expId || !prereqs.error) return;
    Sentry.captureException(prereqs.error, {
      tags: { feature: 'experiments-resume', stage: 'prereqs' },
      extra: { expId, targetStep },
    });
    toast.error(t('resume.error'));
    void navigate({ to: '/experiments' });
  }, [expId, prereqs.error, navigate, t, targetStep]);

  // -- Edge: no channel could be resolved (rollout fallback failed) ----
  useEffect(() => {
    if (!expId || !draftQuery.isSuccess) return;
    if (resolvedChannel !== null) return;
    Sentry.addBreadcrumb({
      category: 'experiments-resume',
      level: 'warning',
      message: 'Resume failed: no channel on draft and no fallback connection available',
      data: { expId },
    });
    toast.error(t('resume.missingChannel'));
    void navigate({ to: '/experiments/create' });
  }, [expId, draftQuery.isSuccess, resolvedChannel, navigate, t]);

  // -- Hydrate on first ready ------------------------------------------
  useEffect(() => {
    if (!expId) return;
    if (!draftQuery.isSuccess) return;
    if (!prereqs.isReady) return;
    if (!resolvedChannel) return;
    // Already hydrated for this draft — bail. The store's `draftId` is set
    // by `hydrate(...)` and stays equal to `expId` for the lifetime of the
    // resumed wizard, which makes background refetches of the draft query
    // a no-op here.
    if (storedDraftId === expId) return;

    const dto = draftQuery.data;

    // Don't restore experiments that have already left Draft — they're
    // lifecycle-locked and any PATCH would 409. Instead redirect the user to
    // wherever they should actually be (enrichment funnel or detail page).
    if (dto.status !== 'DRAFT') {
      toast.error(t('resume.alreadyLaunched'));
      void navigate(resolveNonDraftDestination(dto));
      return;
    }

    // Sentry breadcrumb on rollout fallback so we can verify when the
    // backend ships `channel` and the fallback can be deleted.
    if (!dto.channel) {
      Sentry.addBreadcrumb({
        category: 'experiments-resume',
        level: 'info',
        message: 'Channel fallback applied (draft DTO missing `channel`)',
        data: { expId, fallbackCode: FALLBACK_CHANNEL_CODE },
      });
    }

    const partial = hydrateFromDraft({
      dto,
      contentAttributes: prereqs.contentAttributes,
      fallbackConnectionId,
    });
    hydrate(partial);
  }, [
    expId,
    draftQuery.isSuccess,
    draftQuery.data,
    prereqs.isReady,
    prereqs.contentAttributes,
    fallbackConnectionId,
    resolvedChannel,
    hydrate,
    storedDraftId,
    navigate,
    t,
  ]);

  // No resume requested → render the wizard normally.
  if (!expId) return <>{children}</>;

  // Draft fetch failed → subsidiary-aware recovery. A shared link to an
  // experiment owned by another subsidiary auto-switches (when the backend
  // names the owner) or offers a deep-link-preserving manual switch, instead
  // of bouncing to /experiments with a generic "resume error".
  if (draftQuery.isError) {
    return (
      <div className="bg-gradient-page flex min-h-[70vh] items-center justify-center p-6">
        <ExperimentLoadError experimentId={expId} error={draftQuery.error} />
      </div>
    );
  }

  // Resume requested but the store hasn't been hydrated for THIS expId yet → overlay.
  if (storedDraftId !== expId) return <ResumeOverlay />;

  return <>{children}</>;
}

/**
 * Pick the channel from the DTO when present; otherwise apply the rollout
 * fallback (Google + tenant's Google connection). Returns `null` when
 * neither is available — the wizard then routes the user back to the
 * channel picker.
 */
function resolveChannel(
  dto: DraftExperiment | undefined,
  fallbackConnectionId: string | null,
): ExperimentChannel | null {
  if (!dto) return null;
  if (dto.channel?.connectionId && dto.channel.code) {
    return { connectionId: dto.channel.connectionId, code: dto.channel.code };
  }
  if (fallbackConnectionId) {
    return { connectionId: fallbackConnectionId, code: FALLBACK_CHANNEL_CODE };
  }
  return null;
}

/**
 * Resolve the correct navigation target for an experiment that has already
 * left DRAFT. Mirrors the `onRowClick` routing in `experiment-table.tsx`:
 * - CREATED with a known phase → land on the matching funnel step
 * - CREATED without a phase (pre-trigger) → funnel entry (step 0)
 * - LIVE phase → detail page (enrichment complete, nothing left in funnel)
 * - RUNNING / COMPLETED / anything else → detail page
 */
function resolveNonDraftDestination(dto: DraftExperiment) {
  if (dto.status === 'CREATED') {
    const phaseTarget = resolvePhaseTarget(dto.experimentPhase);
    if (typeof phaseTarget === 'number') {
      return {
        to: FUNNEL_PATH_BY_STEP[phaseTarget],
        params: { expId: dto.id },
      };
    }
    // phaseTarget === 'exit' (LIVE) or null (pre-trigger) both default to
    // funnel entry — the funnel's own guards will advance if needed.
    if (phaseTarget !== 'exit') {
      return {
        to: '/experiments/$expId/content-enrichment' as const,
        params: { expId: dto.id },
      };
    }
  }
  // RUNNING, COMPLETED, CANCELLED, or LIVE phase → detail page.
  return { to: '/experiments/$id' as const, params: { id: dto.id } };
}
